/**
 * The issue tracker's schema, declared once for r2db.
 *
 * The same four normalized tables as `apps/example-issue-tracker` — `user`, `issue`, `tag`,
 * `comment` — but declared in TypeScript rather than SQL, because that is how an r2db database is
 * created: `r2db create --schema ./schema.ts` sends this declaration and the service makes the
 * tables and indexes from it.
 *
 * # Every `.index()` here is paired with a query that needs it
 *
 * r2db runs on a Durable Object over R2, and that home never builds an index on demand: a filter,
 * order or join no declared index covers does not fail, it SCANS, and a scan is what runs into the
 * per-request work budget once the table has data. So each index below names the query shape it
 * serves, and `test/schema.test.ts` walks every query the app issues and fails when one is not
 * covered.
 *
 * Every column is non-null. An unassigned issue's owner is the `"unassigned"` sentinel, not null,
 * for the reason the tracker gives: a null join key never matches (`null != null`), and this schema
 * joins on its owner column.
 *
 * # The project is the bound, and that is the whole reason it exists
 *
 * Every list, board and search is scoped to one project, which reduces every shape here to one
 * sentence: **seek the project's slice of `issue_by_project_created`, stream it, filter.**
 *
 * That matters because of what it does to the two kinds of work differently:
 *
 * - **A window was already a seek**, with or without a project — it has a limit. What the project
 *   changes is what the window is OVER: the newest 50 issues in one project is a tracker, and the
 *   newest 50 in a 7-million-issue store is not a product anyone has.
 * - **A filter was not bounded at all.** Substring search cannot be an index seek, because an
 *   `ILIKE '%x%'` seeks no prefix (`rindle-index-advice` says so directly), and neither can an
 *   `EXISTS` whose inner predicate is one. Unscoped, those are a stream of the whole table with no
 *   stopping condition. Scoped, they stream ~10,000 rows that are ADJACENT in the index — ~40 leaf
 *   pages at 64 KiB, coalescing to ~2 ranged GETs and 2.6 MB of leaf.
 *
 * So **what a scan costs is what sizes a project**, not taste. Three ceilings bound it, and it is
 * worth being precise about which one binds, because this comment previously named the wrong one
 * and the builder's constant was derived from it:
 *
 * - **R2 GETs — NOT the binding constraint.** `DEFAULT_LIMITS.r2Gets` is 256 per call. A project's
 *   slice is contiguous, so `plan.js` coalesces a scan of `W` pages into
 *   `(parts touched) + (layers holding any of them)` GETs — at 64 pages per 4 MiB part, 40 leaf
 *   pages is ~2 GETs, and even 144 would be ~4. The old argument compared leaf PAGES to a GET
 *   ceiling (and to 128 rather than 256), which is a unit error: it read 36,000 issues as "~140
 *   leaf pages and over budget" when the real cost is under 2% of the ceiling. Rounds do not bind
 *   either — `MAX_ROUNDS` is 64 and bounds a query's dependency DEPTH, while a scan is few rounds
 *   and many GETs (`supervisor.js` is explicit about this).
 * - **Thread time — this one binds first.** `DEFAULT_LIMITS.ms` is 10,000 ms of thread time, with
 *   fetch latency subtracted, and it scales with rows scanned rather than with anything R2 does.
 * - **The leaf cache — this one sets the ceiling in practice.** `LEAF_LRU_MAX` is 32 MiB, so at
 *   2.6 MB per project slice about twelve slices coexist in one object. Bigger projects mean
 *   concurrent viewers on different projects evicting each other, which costs a cold fetch on the
 *   *second* search rather than a refusal on the first — a degradation no ceiling reports.
 *
 * 10,000 was chosen against a measurement rather than arithmetic: a substring scan of the largest
 * project in the then-deployed 21.6 GB base (`seed-proj-001090`, 9,600 issues) returned effectively
 * instantly, so 10,000 is the largest tested slice made typical. The 100 GB base now deployed is
 * built at that average, with its largest project at the 15,000 cap. The base builder
 * (`rust/rindle-paged/examples/build_issues_store.rs`) derives its project count from the target
 * size for that reason, and CAPS the skew at 1.5x — a partition bounds nothing unless the
 * partitions are bounded, and a real tracker's projects are very unequal. The substring scan also
 * carries a deadline, so an unlucky project degrades visibly rather than spending the whole budget.
 *
 * One thing this deliberately does NOT claim: that any particular axis becomes a seek. `owner:` and
 * `tag:` are `EXISTS` over a join, and the planner decides per query whether to drive from the
 * parent or flip to the child (`rindle-planner`'s `flip_bar` bars only `NOT EXISTS` and aggregates;
 * `PagedCostModel` samples real fanouts to choose). Flipping `tag:billing` unscoped would drive
 * ~1.8M tag rows, which is why the project — not the index, and not the exact value — is what makes
 * these affordable. Exact values from the typeahead are still worth having, because they turn each
 * row's inner check into a comparison instead of a substring match.
 */
import { createSchema, number, string, table } from 'r2db';

export const ISSUE_STATUSES = ['backlog', 'todo', 'in-progress', 'review', 'done'] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];
export const ISSUE_PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const;
export type IssuePriority = (typeof ISSUE_PRIORITIES)[number];

export const project = table('project')
  .columns({ id: string(), name: string(), key: string() })
  // The switcher: every project, alphabetized. Bounded by construction — a tracker has thousands
  // of projects and millions of issues, so this is the one table a page can hold all of.
  .index('project_by_name', 'name')
  .primaryKey('id');

export const user = table('user')
  .columns({ id: string(), name: string() })
  // The user picker: the whole (small) table, alphabetized.
  .index('user_by_name', 'name')
  .primaryKey('id');

export const issue = table('issue')
  .columns({
    id: string(),
    projectId: string(),
    title: string(),
    status: string<IssueStatus>(),
    priority: string<IssuePriority>(),
    ownerId: string(),
    createdAt: number(),
    updatedAt: number(),
  })
  // One project's list window: newest first, `id` as the tiebreak, so a page boundary is a seek.
  // This is also what makes a project-scoped substring scan contiguous — see the note above.
  .index('issue_by_project_created', 'projectId', ['createdAt', 'desc'], 'id')
  // One project's board column: the same window with a status filter in front of it.
  .index('issue_by_project_status_created', 'projectId', 'status', ['createdAt', 'desc'], 'id')
  // The cross-project feed, which the activity view and the "everything" list read. Kept despite
  // the partition because it is still a seek with a limit — the partition is what bounds SCANS,
  // and this does not scan.
  .index('issue_by_created', ['createdAt', 'desc'], 'id')
  // The owner join's reverse direction — a renamed user re-finding the issues it owns — and the
  // "mine" view, which is deliberately cross-project.
  //
  // It is NOT what the search box's `owner:` axis should read, and that is the one thing about
  // this schema a reader would otherwise get backwards: the index is cross-project, so seeking it
  // for one owner inside one project reads that owner's whole ~226,000-row slice to answer from
  // the ~83 of them the project holds. Both this index and `issue_by_project_created` seek one of
  // that query's two pinned columns and both stream its order, so the leaf chooses between them on
  // the counted rows per seek key — 10,000 against 226,000. `queries.ts`'s `issueConditions` carries
  // the measurement, and the two spellings that did not finish before this one.
  .index('issue_by_owner', 'ownerId', ['createdAt', 'desc'], 'id')
  .primaryKey('id');

export const tag = table('tag')
  .columns({ id: string(), issueId: string(), name: string() })
  // An issue's tags, alphabetized: the forward direction of the tag join.
  .index('tag_by_issue', 'issueId', 'name')
  // Every issue carrying a tag: the search box's `tag:` axis.
  .index('tag_by_name', 'name')
  .primaryKey('id');

export const comment = table('comment')
  .columns({ id: string(), issueId: string(), authorId: string(), body: string(), createdAt: number() })
  // An issue's thread, oldest first (the earliest comment is the description), and its count.
  .index('comment_by_issue', 'issueId', 'createdAt')
  // The activity feed: the newest comments across every issue.
  .index('comment_by_created', ['createdAt', 'desc'], 'id')
  // The author join's reverse direction.
  .index('comment_by_author', 'authorId')
  .primaryKey('id');

export const schema = createSchema({ tables: [project, user, issue, tag, comment] });
